/**
 * Live Notification Service
 * Connects to the main backend via Socket.IO (same port as the REST API).
 * Used by TopBar and any other component that needs real-time alerts.
 */
import { io } from 'socket.io-client';
import { getNotifURL } from '../utils/api';

const NOTIF_SERVER = getNotifURL();
const LOCAL_CACHE_PREFIX = 'kaaryaalaya:notifications:';
const SOCKET_IO_PATH = '/api/socket.io';

let socket = null;

function cacheKey(userId) {
  return `${LOCAL_CACHE_PREFIX}${String(userId)}`;
}

function isSameNotification(left, right) {
  if (!left || !right) return false;
  if (left.id === right.id) return true;
  return (
    left.title === right.title &&
    left.link === right.link &&
    (left.message || '') === (right.message || '')
  );
}

function mergeNotificationLists(existing, incoming) {
  const merged = [...existing];

  incoming.forEach((nextNotif) => {
    const index = merged.findIndex((current) => isSameNotification(current, nextNotif));
    if (index >= 0) {
      merged[index] = { ...merged[index], ...nextNotif };
      return;
    }
    merged.push(nextNotif);
  });

  return merged.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

function readLocalNotifications(userId) {
  if (!userId) return [];
  try {
    const raw = window.localStorage.getItem(cacheKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocalNotifications(userId, notifications) {
  if (!userId) return;
  try {
    window.localStorage.setItem(cacheKey(userId), JSON.stringify(notifications));
  } catch {
    // Ignore storage failures
  }
}

function saveLocalNotification(userId, notification) {
  const cached = readLocalNotifications(userId);
  writeLocalNotifications(userId, mergeNotificationLists(cached, [notification]));
}

function markLocalNotificationRead(id, userId) {
  const cached = readLocalNotifications(userId).map((notif) => (
    notif.id === id ? { ...notif, isRead: true } : notif
  ));
  writeLocalNotifications(userId, cached);
}

function markAllLocalNotificationsRead(userId) {
  const cached = readLocalNotifications(userId).map((notif) => ({ ...notif, isRead: true }));
  writeLocalNotifications(userId, cached);
}

function deleteLocalNotification(id, userId) {
  const cached = readLocalNotifications(userId).filter((notif) => notif.id !== id);
  writeLocalNotifications(userId, cached);
}

function reconcileLocalNotifications(userId, serverNotifications) {
  const cached = readLocalNotifications(userId);
  const remainingLocal = cached.filter(
    (localNotif) => !serverNotifications.some((serverNotif) => isSameNotification(localNotif, serverNotif)),
  );
  writeLocalNotifications(userId, remainingLocal);
  return mergeNotificationLists(remainingLocal, serverNotifications);
}

/** Establish connection and join the user's private room. */
function connect(userId, userType = 'admin', userName = '', staffId = null) {
  if (socket && socket.connected) return;
  socket = io(NOTIF_SERVER, {
    path: SOCKET_IO_PATH,
    withCredentials: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 3000,
    transports: ['polling'],
    upgrade: false,
  });
  socket.on('connect', () => {
    socket.emit('join', {
      userId: String(userId),
      userType: String(userType || 'admin'),
      userName: String(userName || '').trim(),
      staffId: staffId == null ? null : String(staffId),
    });
  });
}

/** Tear down the socket (call on component unmount / logout). */
function disconnect() {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

/** Register a callback for incoming new notifications. */
function onNewNotification(cb) {
  if (!socket) return () => {};
  socket.on('new_notification', cb);
  return () => socket.off('new_notification', cb);
}

/** Register a callback for when a single notification is marked read (cross-tab sync). */
function onNotificationRead(cb) {
  if (!socket) return () => {};
  socket.on('notification_read', cb);
  return () => socket.off('notification_read', cb);
}

/** Register a callback for when all notifications are marked read (cross-tab sync). */
function onAllRead(cb) {
  if (!socket) return () => {};
  socket.on('all_read', cb);
  return () => socket.off('all_read', cb);
}

/** Register a callback for when a notification is deleted (cross-tab sync). */
function onNotificationDeleted(cb) {
  if (!socket) return () => {};
  socket.on('notification_deleted', cb);
  return () => socket.off('notification_deleted', cb);
}

function onTeamworkCountUpdated(cb) {
  if (!socket) return () => {};
  socket.on('teamwork_count_updated', cb);
  return () => socket.off('teamwork_count_updated', cb);
}

function onTeamworkMessageAlert(cb) {
  if (!socket) return () => {};
  socket.on('teamwork_message_alert', cb);
  return () => socket.off('teamwork_message_alert', cb);
}

// ── REST helpers ──────────────────────────────────────────────────────────────

/** Fetch paginated notifications for a user. */
async function fetchNotifications(userId, { limit = 30, offset = 0 } = {}) {
  try {
    const res = await fetch(
      `${NOTIF_SERVER}/api/notifications?userId=${encodeURIComponent(userId)}&limit=${limit}&offset=${offset}`,
    );
    if (!res.ok) {
      return readLocalNotifications(userId);
    }
    const data = await res.json();
    return reconcileLocalNotifications(userId, data.notifications || []);
  } catch {
    return readLocalNotifications(userId);
  }
}

/** Mark a single notification as read. */
async function markRead(id, userId) {
  markLocalNotificationRead(id, userId);
  await fetch(`${NOTIF_SERVER}/api/notifications/${id}/read`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId: String(userId) }),
  });
}

/** Mark all notifications as read for a user. */
async function markAllRead(userId) {
  markAllLocalNotificationsRead(userId);
  await fetch(`${NOTIF_SERVER}/api/notifications/read-all?userId=${encodeURIComponent(userId)}`, {
    method: 'PATCH',
  });
}

/** Delete a notification. */
async function deleteNotification(id, userId) {
  deleteLocalNotification(id, userId);
  await fetch(`${NOTIF_SERVER}/api/notifications/${id}?userId=${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  });
}

const NotificationService = {
  connect,
  disconnect,
  onNewNotification,
  onNotificationRead,
  onAllRead,
  onNotificationDeleted,
  onTeamworkCountUpdated,
  onTeamworkMessageAlert,
  fetchNotifications,
  markRead,
  markAllRead,
  deleteNotification,
  saveLocalNotification,
  readLocalNotifications,
  mergeNotificationLists,
};

export default NotificationService;
